import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Users, Wifi } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "uniplay_visit_tracked";
const SESSION_ID_KEY = "uniplay_session_id";
const HEARTBEAT_INTERVAL_MS = 20000; // every 20s

function getSessionId() {
  let sid = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sid) {
    sid =
      (crypto?.randomUUID && crypto.randomUUID()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sid);
  }
  return sid;
}

export default function VisitorCounter() {
  const [count, setCount] = useState(null);
  const [online, setOnline] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const loadTotal = async () => {
      try {
        const alreadyTracked = sessionStorage.getItem(SESSION_KEY);
        const endpoint = alreadyTracked ? "/visits/count" : "/visits/track";
        const method = alreadyTracked ? "get" : "post";
        const res = await axios[method](`${API}${endpoint}`);
        if (!cancelled && typeof res.data?.count === "number") {
          setCount(res.data.count);
          if (!alreadyTracked) sessionStorage.setItem(SESSION_KEY, "1");
        }
      } catch (e) {
        // silent
      }
    };

    const sendHeartbeat = async () => {
      try {
        const res = await axios.post(`${API}/visits/heartbeat`, {
          session_id: getSessionId(),
        });
        if (!cancelled && typeof res.data?.online === "number") {
          setOnline(res.data.online);
        }
      } catch (e) {
        // silent
      }
    };

    loadTotal();
    sendHeartbeat();
    timerRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === "visible") sendHeartbeat();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const nf = new Intl.NumberFormat("pt-BR");
  const totalFmt = count === null ? "—" : nf.format(count);
  const onlineFmt = online === null ? "—" : nf.format(online);

  return (
    <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
      <div
        data-testid="visitor-counter"
        className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm"
      >
        <span className="relative flex w-2 h-2">
          <span className="absolute inline-flex w-full h-full rounded-full bg-red-500 opacity-75 animate-ping" />
          <span className="relative inline-flex w-2 h-2 rounded-full bg-red-600" />
        </span>
        <Users className="w-3.5 h-3.5 text-red-500" />
        <span className="text-[11px] text-white/60 uppercase tracking-wider font-semibold">
          Visitantes:
        </span>
        <span
          data-testid="visitor-counter-number"
          className="text-sm font-extrabold text-white tabular-nums"
        >
          {totalFmt}
        </span>
      </div>

      <div
        data-testid="online-counter"
        className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-[#25D366]/10 border border-[#25D366]/30 backdrop-blur-sm"
      >
        <span className="relative flex w-2 h-2">
          <span className="absolute inline-flex w-full h-full rounded-full bg-[#25D366] opacity-75 animate-ping" />
          <span className="relative inline-flex w-2 h-2 rounded-full bg-[#25D366]" />
        </span>
        <Wifi className="w-3.5 h-3.5 text-[#25D366]" />
        <span className="text-[11px] text-white/60 uppercase tracking-wider font-semibold">
          Online agora:
        </span>
        <span
          data-testid="online-counter-number"
          className="text-sm font-extrabold text-white tabular-nums"
        >
          {onlineFmt}
        </span>
      </div>
    </div>
  );
}
